import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  aBlancEquivalents,
  checkInTime,
  comfortFloor,
  formatFormula,
  resolveMenage,
  tradeoffFrontier,
  DEMAND_METRICS,
  DEMAND_METRIC_LABELS,
  staffingConfigFromRows,
  windowsOverlap,
  type BoardActivity,
  type DemandMetric,
  type RoomWorkload,
  type StaffingConfigSnapshot,
  type WorkContentRow,
  type WorkContentUpsert,
} from '@lemontaigu/staff';
import { useBoard, useStaffingConfig } from '@lemontaigu/staff/react';
import { timeInputValue } from './logic.js';

// « Staffing » (Opérations group, operator-only) — the configuration behind
// « how many people does tomorrow need », per docs/PREDICTIVE-STAFFING-BRIEF.md
// §4. The Activités tab says what activities EXIST; this says how many people
// each one takes.
//
// LABEL — « Staffing », deliberately, not « Effectifs ». The English term is
// already the house word (the staff WhatsApp group is « Le Montaigu Staff »), so
// this is an operator-sanctioned anglicism rather than an oversight. Recorded in
// docs/GLOSSARY.md so a later pass does not "correct" it.
//
// The three archetypes are edited very differently on purpose, because they ARE
// different: a binary post is a threshold, work content is a set of minutes, and
// a tiered profile is a list of bands whose post list changes shape. A single
// generic form over all three would hide exactly what the operator needs to see.

/** Numbers the preview is computed against — a plausible mid-season day. */
const PREVIEW: RoomWorkload = { aBlancs: 12, turnovers: 12, recouches: 20, recouchesAFond: 2, arrivees: 12 };

type Archetype = 'binary' | 'workContent' | 'tiered';

interface NewProfile {
  label: string;
  archetype: Archetype;
  /** Work-content only: the activity the derived window belongs to. */
  activityId: string;
  /** Binary/tiered: the demand metric and the threshold the band opens at. */
  metric: DemandMetric;
  threshold: string;
}

interface NewPost {
  bandId: number;
  activityId: string;
  label: string;
  windowStart: string;
  windowEnd: string;
  heads: string;
}

const emptyNewProfile = (): NewProfile => ({
  label: '',
  archetype: 'workContent',
  activityId: '',
  metric: 'covers',
  threshold: '20',
});

/** A one-term formula is enough to start; the operator refines it in Activités. */
function formulaFor(draft: NewProfile): Record<string, number> {
  return { [draft.metric]: 1 };
}

const ARCHETYPE_LABELS: Record<Archetype, string> = {
  workContent: 'Charge de travail (minutes par chambre)',
  binary: 'Poste 0 ou 1 (ouvert au-delà d’un seuil)',
  tiered: 'Paliers (l’organisation change avec le volume)',
};

function workContentDraft(row: WorkContentRow): WorkContentUpsert {
  return {
    minutesABlanc: row.minutes_a_blanc,
    minutesRecouche: row.minutes_recouche,
    minutesRecoucheFond: row.minutes_recouche_fond,
    minutesArrivee: row.minutes_arrivee,
    comfortFloorEquivalents: row.comfort_floor_equivalents,
    deepCleanCadence: row.deep_clean_cadence,
    earliestStart: timeInputValue(row.earliest_start),
    defaultStart: timeInputValue(row.default_start),
    latestEnd: timeInputValue(row.latest_end),
    heavyArrivals: row.heavy_arrivals,
    checkInEarly: timeInputValue(row.check_in_early),
    checkInNormal: timeInputValue(row.check_in_normal),
    maxHeads: row.max_heads ?? 6,
    targetFinish: row.target_finish ? timeInputValue(row.target_finish) : null,
  };
}

export function Staffing() {
  const board = useBoard();
  const staffing = useStaffingConfig();
  const [snapshot, setSnapshot] = useState<StaffingConfigSnapshot | null>(null);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ profileId: number; values: WorkContentUpsert } | null>(null);
  const [pair, setPair] = useState<{ a: string; b: string }>({ a: '', b: '' });
  const [creating, setCreating] = useState<NewProfile | null>(null);
  const [newBand, setNewBand] = useState<{ profileId: number; from: string } | null>(null);
  const [newPost, setNewPost] = useState<NewPost | null>(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [config, acts] = await Promise.all([staffing.load(), board.activities()]);
      setSnapshot(config);
      setActivities(acts);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [staffing, board]);

  useEffect(() => {
    void load();
  }, [load]);

  const byActivityId = useMemo(() => new Map(activities.map((a) => [a.id, a])), [activities]);

  // The resolved view of what is configured — the same pure assembler the
  // resolver uses, so the editor cannot show one thing and the board another.
  const assembled = useMemo(
    () =>
      snapshot
        ? staffingConfigFromRows({
            profiles: snapshot.profiles,
            bands: snapshot.bands,
            posts: snapshot.posts,
            workContent: snapshot.workContent,
          })
        : null,
    [snapshot],
  );

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setErr('');
    try {
      await action();
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Create a profile and, for a work-content one, everything it needs to be
   * usable in the same gesture: its band, its anchor post, and the parameter row
   * pre-filled with the operator's own figures (30/10/30/5).
   *
   * Doing this in one step rather than three is not a convenience — a
   * work-content profile without its parameter row is one of the « profils
   * incomplets » the resolver skips, so making the operator assemble it by hand
   * would mean the first thing they see after creating a profile is a warning
   * that it does not work.
   */
  const createProfile = async (draft: NewProfile) => {
    const profileId = await staffing.saveProfile(null, {
      label: draft.label.trim(),
      archetype: draft.archetype,
      demandFormula: draft.archetype === 'workContent' ? null : formulaFor(draft),
      active: true,
      sort: 100,
      note: null,
    });

    if (draft.archetype === 'workContent') {
      const bandId = await staffing.saveBand(null, profileId, 0);
      await staffing.savePost(null, bandId, {
        activityId: Number(draft.activityId),
        label: draft.label.trim(),
        // The window is DERIVED per day for this archetype (§4.4); these bounds
        // are a placeholder the model never reads.
        windowStart: '09:00',
        windowEnd: '15:00',
        heads: 1,
        sort: 100,
      });
      await staffing.saveWorkContent(profileId, {
        minutesABlanc: 30,
        minutesRecouche: 10,
        minutesRecoucheFond: 30,
        minutesArrivee: 5,
        comfortFloorEquivalents: 10,
        deepCleanCadence: 4,
        earliestStart: '08:00',
        defaultStart: '09:00',
        latestEnd: '17:00',
        heavyArrivals: 15,
        checkInEarly: '14:00',
        checkInNormal: '15:00',
        maxHeads: 6,
        // No target by default: the historic behaviour is « the smallest team
        // that fits the day », and inventing a deadline would silently add
        // people the operator never asked for (Hard Rule 1).
        targetFinish: null,
      });
    } else {
      // A binary profile is one band at its threshold; a tiered one starts with
      // its lowest band. Either way the operator gets something addressable
      // rather than an empty shell.
      await staffing.saveBand(null, profileId, Number(draft.threshold) || 0);
    }
  };

  /**
   * Do two activities' windows actually overlap?
   *
   * Only an OVERLAPPING pair needs declaring: two posts that merely follow each
   * other are always holdable by one body, so a row for them is inert. Saying so
   * in the UI is worth more than the hint above it — the operator's first
   * instinct was to declare a sequential pair, which is a reasonable reading of
   * « cumulable » and costs nothing, but leaves them wondering whether it worked.
   */
  const pairOverlaps = (a: number, b: number): boolean => {
    const x = byActivityId.get(a);
    const y = byActivityId.get(b);
    if (!x || !y) return true; // unknown → say nothing rather than guess
    return windowsOverlap(
      { activityId: x.id, label: x.label, windowStart: x.windowStart.slice(0, 5), windowEnd: x.windowEnd.slice(0, 5), heads: 1 },
      { activityId: y.id, label: y.label, windowStart: y.windowStart.slice(0, 5), windowEnd: y.windowEnd.slice(0, 5), heads: 1 },
    );
  };

  const activityLabel = (id: number | null): string =>
    id === null ? '— (poste fusionné)' : byActivityId.get(id)?.label ?? `#${id}`;

  return (
    <div className="planning">
      <div className="equipe-head">
        <h2 className="section-title">Staffing</h2>
        <span className="equipe-hint">
          Combien de personnes il faut, et quand. L’onglet Activités dit ce qui existe ; cette page dit ce que ça coûte
          en personnel.
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {snapshot === null && <p className="staff-empty">Chargement…</p>}

      {snapshot !== null && snapshot.profiles.length === 0 && (
        <p className="staff-empty">
          Aucun profil de staffing. Rien n’est configuré : le tableau continue de fonctionner comme avant (seuil par
          activité). Les profils créés ici ne prennent effet qu’une fois la bascule faite.
        </p>
      )}

      {snapshot !== null && (
        <section className="planning-card">
          <header className="planning-card-head">
            <strong>Nouveau profil</strong>
            <span className="equipe-hint">
              Un profil par façon de compter le personnel. Le ménage se calcule en minutes par chambre ; le bar est un
              poste ouvert ou fermé ; le dîner change d’organisation au-delà d’un seuil.
            </span>
          </header>

          {creating === null ? (
            <button type="button" className="planning-add" disabled={busy} onClick={() => setCreating(emptyNewProfile())}>
              + Créer un profil
            </button>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (creating.label.trim() === '') return;
                if (creating.archetype === 'workContent' && !creating.activityId) return;
                void run(async () => {
                  await createProfile(creating);
                  setCreating(null);
                });
              }}
            >
              <div className="staffing-form">
                <label>
                  <span>Nom</span>
                  <input
                    value={creating.label}
                    placeholder="Ménage"
                    onChange={(e) => setCreating({ ...creating, label: e.target.value })}
                  />
                </label>
                <label>
                  <span>Type</span>
                  <select
                    value={creating.archetype}
                    onChange={(e) => setCreating({ ...creating, archetype: e.target.value as Archetype })}
                  >
                    {(Object.keys(ARCHETYPE_LABELS) as Archetype[]).map((a) => (
                      <option key={a} value={a}>
                        {ARCHETYPE_LABELS[a]}
                      </option>
                    ))}
                  </select>
                </label>

                {creating.archetype === 'workContent' ? (
                  <label>
                    <span>Activité concernée</span>
                    <select
                      value={creating.activityId}
                      onChange={(e) => setCreating({ ...creating, activityId: e.target.value })}
                    >
                      <option value="">Activité…</option>
                      {activities.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <>
                    <label>
                      <span>Calculé sur</span>
                      <select
                        value={creating.metric}
                        onChange={(e) => setCreating({ ...creating, metric: e.target.value as DemandMetric })}
                      >
                        {DEMAND_METRICS.map((m) => (
                          <option key={m} value={m}>
                            {DEMAND_METRIC_LABELS[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>{creating.archetype === 'binary' ? 'Ouvert à partir de' : 'Premier palier à partir de'}</span>
                      <input
                        type="number"
                        min={0}
                        value={creating.threshold}
                        onChange={(e) => setCreating({ ...creating, threshold: e.target.value })}
                      />
                    </label>
                  </>
                )}
              </div>

              <p className="equipe-hint">
                {creating.archetype === 'workContent'
                  ? 'Créé avec vos minutes par défaut (à blanc 30, recouche 10, à fond 30, arrivée 5) et l’horaire glissant 8 h–17 h. Tout est modifiable ensuite.'
                  : 'Le palier est créé vide : ajoutez ensuite les postes (qui, quand, combien).'}
              </p>

              <button
                type="submit"
                className="planning-add"
                disabled={busy || creating.label.trim() === '' || (creating.archetype === 'workContent' && !creating.activityId)}
              >
                Créer
              </button>
              <button type="button" className="planning-add" disabled={busy} onClick={() => setCreating(null)}>
                Annuler
              </button>
            </form>
          )}
        </section>
      )}

      {assembled !== null && assembled.problems.length > 0 && (
        <div className="planning-card">
          <header className="planning-card-head">
            <strong>Profils incomplets</strong>
          </header>
          <ul>
            {assembled.problems.map((p) => (
              <li key={p.profileId}>
                {p.label} — {p.reason}
              </li>
            ))}
          </ul>
          <p className="equipe-hint">
            Un profil incomplet est simplement ignoré : l’activité n’a pas de besoin calculé, elle ne casse pas le
            tableau.
          </p>
        </div>
      )}

      {snapshot !== null &&
        snapshot.profiles.map((profile) => {
          const bands = snapshot.bands
            .filter((b) => b.profile_id === profile.id)
            .sort((a, b) => a.from_demand - b.from_demand);
          const work = snapshot.workContent.find((w) => w.profile_id === profile.id);
          const editing = draft?.profileId === profile.id ? draft.values : null;

          return (
            <section key={profile.id} className="planning-card">
              <header className="planning-card-head">
                <strong>{profile.label}</strong>
                <button
                  type="button"
                  disabled={busy}
                  title="Supprimer ce profil et tout ce qu’il contient"
                  onClick={() => void run(() => staffing.deleteProfile(profile.id))}
                >
                  Supprimer
                </button>
                <span className="equipe-hint">
                  {profile.archetype === 'workContent'
                    ? 'charge de travail'
                    : profile.archetype === 'binary'
                      ? 'poste 0 ou 1'
                      : 'paliers'}
                  {profile.demand_formula ? ` · ${formatFormula(profile.demand_formula)}` : ''}
                  {profile.active ? '' : ' · inactif'}
                </span>
              </header>

              {/* ── Work content: the person-minutes ─────────────────────── */}
              {profile.archetype === 'workContent' && work && (
                <div>
                  {editing === null ? (
                    <>
                      <table className="staff-table">
                        <tbody>
                          <tr>
                            <td>À blanc (départ)</td>
                            <td>{work.minutes_a_blanc} min</td>
                            <td>Recouche</td>
                            <td>{work.minutes_recouche} min</td>
                          </tr>
                          <tr>
                            <td>Recouche à fond</td>
                            <td>{work.minutes_recouche_fond} min</td>
                            <td>Arrivée (préparation)</td>
                            <td>{work.minutes_arrivee} min</td>
                          </tr>
                          <tr>
                            <td>Deux personnes au-delà de</td>
                            <td>{work.comfort_floor_equivalents} à blancs équivalents</td>
                            <td>À fond toutes les</td>
                            <td>{work.deep_clean_cadence} nuits</td>
                          </tr>
                          <tr>
                            <td>Début au plus tôt</td>
                            <td>{timeInputValue(work.earliest_start)}</td>
                            <td>Début par défaut</td>
                            <td>{timeInputValue(work.default_start)}</td>
                          </tr>
                          <tr>
                            <td>Fin de service</td>
                            <td>{timeInputValue(work.latest_end)}</td>
                            <td>Arrivées « chargées » au-delà de</td>
                            <td>{work.heavy_arrivals}</td>
                          </tr>
                        </tbody>
                      </table>
                      <Preview params={previewFromRow(work)} />
                      <button type="button" className="planning-add" disabled={busy} onClick={() => setDraft({ profileId: profile.id, values: workContentDraft(work) })}>
                        Modifier
                      </button>
                    </>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        void run(async () => {
                          await staffing.saveWorkContent(profile.id, editing);
                          setDraft(null);
                        });
                      }}
                    >
                      <div className="staffing-form">
                        <NumberField label="À blanc (min)" value={editing.minutesABlanc} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, minutesABlanc: v } })} />
                        <NumberField label="Recouche (min)" value={editing.minutesRecouche} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, minutesRecouche: v } })} />
                        <NumberField label="Recouche à fond (min)" value={editing.minutesRecoucheFond} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, minutesRecoucheFond: v } })} />
                        <NumberField label="Arrivée (min)" value={editing.minutesArrivee} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, minutesArrivee: v } })} />
                        <NumberField label="Seuil 2 personnes (équivalents)" value={editing.comfortFloorEquivalents} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, comfortFloorEquivalents: v } })} />
                        <NumberField label="À fond toutes les N nuits" value={editing.deepCleanCadence} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, deepCleanCadence: v } })} />
                        <NumberField label="Arrivées chargées au-delà de" value={editing.heavyArrivals} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, heavyArrivals: v } })} />
                        <TimeField label="Début au plus tôt" value={editing.earliestStart} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, earliestStart: v } })} />
                        <TimeField label="Début par défaut" value={editing.defaultStart} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, defaultStart: v } })} />
                        <TimeField label="Fin de service" value={editing.latestEnd} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, latestEnd: v } })} />
                        <TimeField label="Check-in avancé" value={editing.checkInEarly} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, checkInEarly: v } })} />
                        <TimeField label="Check-in normal" value={editing.checkInNormal} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, checkInNormal: v } })} />
                        {/* The trade-off controls (ADR-0015 §4). Leaving « terminé
                            avant » empty keeps the historic behaviour: the
                            smallest possible team, finishing when it finishes. */}
                        <NumberField label="Effectif maximum" value={editing.maxHeads} onChange={(v) => setDraft({ profileId: profile.id, values: { ...editing, maxHeads: v } })} />
                        <label>
                          <span>Terminé avant (facultatif)</span>
                          <input
                            type="time"
                            value={editing.targetFinish ?? ''}
                            onChange={(e) =>
                              setDraft({
                                profileId: profile.id,
                                values: { ...editing, targetFinish: e.target.value === '' ? null : e.target.value },
                              })
                            }
                          />
                        </label>
                      </div>
                      <div className="staffing-actions">
                        <button type="submit" className="planning-add" disabled={busy}>
                          Enregistrer
                        </button>
                        <button type="button" className="planning-add" disabled={busy} onClick={() => setDraft(null)}>
                          Annuler
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* ── Bands and their posts ────────────────────────────────── */}
              {profile.archetype !== 'workContent' &&
                bands.map((band) => {
                  const posts = snapshot.posts
                    .filter((p) => p.band_id === band.id)
                    .sort((a, b) => a.sort - b.sort || a.id - b.id);
                  return (
                    <div key={band.id} className="planning-list">
                      <header className="planning-team-head">
                        <span>
                          {profile.archetype === 'binary' ? 'Ouvert à partir de ' : 'À partir de '}
                          {band.from_demand}
                        </span>
                        <button type="button" disabled={busy} onClick={() => void run(() => staffing.deleteBand(band.id))}>
                          Supprimer le palier
                        </button>
                      </header>
                      <table className="staff-table">
                        <tbody>
                          {posts.map((p) => (
                            <tr key={p.id}>
                              <td>{p.label}</td>
                              <td>{activityLabel(p.activity_id)}</td>
                              <td>
                                {timeInputValue(p.window_start)}–{timeInputValue(p.window_end)}
                              </td>
                              <td>×{p.heads}</td>
                              <td>
                                <button type="button" disabled={busy} onClick={() => void run(() => staffing.deletePost(p.id))}>
                                  Retirer
                                </button>
                              </td>
                            </tr>
                          ))}
                          {posts.length === 0 && (
                            <tr>
                              <td colSpan={5} className="staff-empty">
                                Palier sans poste — il est ignoré.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {newPost?.bandId === band.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (newPost.label.trim() === '') return;
                            void run(async () => {
                              await staffing.savePost(null, band.id, {
                                // Null activity = a MERGED post (« Plonge + nettoyage »),
                                // which is a real shape, not a missing value.
                                activityId: newPost.activityId === '' ? null : Number(newPost.activityId),
                                label: newPost.label.trim(),
                                windowStart: newPost.windowStart,
                                windowEnd: newPost.windowEnd,
                                heads: Math.max(0, Number(newPost.heads) || 1),
                                sort: 100,
                              });
                              setNewPost(null);
                            });
                          }}
                        >
                          <div className="staffing-form">
                            <label>
                              <span>Libellé</span>
                              <input value={newPost.label} placeholder="Service" onChange={(e) => setNewPost({ ...newPost, label: e.target.value })} />
                            </label>
                            <label>
                              <span>Activité</span>
                              <select value={newPost.activityId} onChange={(e) => setNewPost({ ...newPost, activityId: e.target.value })}>
                                <option value="">— poste fusionné</option>
                                {activities.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>De</span>
                              <input type="time" value={newPost.windowStart} onChange={(e) => setNewPost({ ...newPost, windowStart: e.target.value })} />
                            </label>
                            <label>
                              <span>À</span>
                              <input type="time" value={newPost.windowEnd} onChange={(e) => setNewPost({ ...newPost, windowEnd: e.target.value })} />
                            </label>
                            <label>
                              <span>Personnes</span>
                              <input type="number" min={0} value={newPost.heads} onChange={(e) => setNewPost({ ...newPost, heads: e.target.value })} />
                            </label>
                          </div>
                          <div className="staffing-actions">
                            <button type="submit" className="planning-add" disabled={busy || newPost.label.trim() === ''}>
                              Ajouter le poste
                            </button>
                            <button type="button" className="planning-add" disabled={busy} onClick={() => setNewPost(null)}>
                              Annuler
                            </button>
                          </div>
                        </form>
                      ) : (
                        <button
                          type="button"
                          className="planning-add"
                          disabled={busy}
                          onClick={() =>
                            setNewPost({ bandId: band.id, activityId: '', label: '', windowStart: '18:00', windowEnd: '21:00', heads: '1' })
                          }
                        >
                          + Poste
                        </button>
                      )}
                    </div>
                  );
                })}

              {profile.archetype === 'tiered' &&
                (newBand?.profileId === profile.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run(async () => {
                        await staffing.saveBand(null, profile.id, Number(newBand.from) || 0);
                        setNewBand(null);
                      });
                    }}
                  >
                    <label>
                      <span>Nouveau palier à partir de</span>
                      <input type="number" min={0} value={newBand.from} onChange={(e) => setNewBand({ ...newBand, from: e.target.value })} />
                    </label>
                    <div className="staffing-actions">
                      <button type="submit" className="planning-add" disabled={busy}>
                        Ajouter le palier
                      </button>
                      <button type="button" className="planning-add" disabled={busy} onClick={() => setNewBand(null)}>
                        Annuler
                      </button>
                    </div>
                  </form>
                ) : (
                  <button type="button" className="planning-add" disabled={busy} onClick={() => setNewBand({ profileId: profile.id, from: '20' })}>
                    + Palier
                  </button>
                ))}
            </section>
          );
        })}

      {/* ── Concurrent combinations ──────────────────────────────────────── */}
      {snapshot !== null && (
        <section className="planning-card">
          <header className="planning-card-head">
            <strong>Postes cumulables</strong>
            <span className="equipe-hint">
              Deux activités qu’UNE personne peut tenir en même temps (le bar et la réception). À déclarer uniquement
              quand les horaires se chevauchent : deux postes qui se suivent sont toujours cumulables.
            </span>
          </header>
          <table className="staff-table">
            <tbody>
              {snapshot.combinations.map((c) => (
                <tr key={`${c.activity_a}:${c.activity_b}`}>
                  <td>{activityLabel(c.activity_a)}</td>
                  <td>
                    + {activityLabel(c.activity_b)}
                    {!pairOverlaps(c.activity_a, c.activity_b) && (
                      <span className="equipe-hint"> — horaires disjoints, déjà cumulables sans déclaration</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void run(() => staffing.setCombination(c.activity_a, c.activity_b, false))}
                    >
                      Retirer
                    </button>
                  </td>
                </tr>
              ))}
              {snapshot.combinations.length === 0 && (
                <tr>
                  <td colSpan={3} className="staff-empty">
                    Aucun poste cumulable déclaré.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const a = Number(pair.a);
              const b = Number(pair.b);
              if (!a || !b || a === b) return;
              void run(async () => {
                await staffing.setCombination(a, b, true);
                setPair({ a: '', b: '' });
              });
            }}
          >
            <select value={pair.a} onChange={(e) => setPair({ ...pair, a: e.target.value })}>
              <option value="">Activité…</option>
              {activities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
            <select value={pair.b} onChange={(e) => setPair({ ...pair, b: e.target.value })}>
              <option value="">Activité…</option>
              {activities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
            <button type="submit" className="planning-add" disabled={busy || !pair.a || !pair.b || pair.a === pair.b}>
              Ajouter
            </button>
            {pair.a !== '' && pair.b !== '' && pair.a !== pair.b && !pairOverlaps(Number(pair.a), Number(pair.b)) && (
              <p className="equipe-hint">
                Ces deux horaires ne se chevauchent pas : une seule personne peut déjà tenir les deux, l’un après
                l’autre. La déclaration est inutile (sans effet).
              </p>
            )}
          </form>
        </section>
      )}
    </div>
  );
}

/**
 * What the current numbers actually produce, on a fixed reference day. The
 * operator is editing minutes, not head counts — this closes the loop so a
 * change of coefficient is visible as a change of staffing rather than being
 * discovered on the board a week later.
 */
interface PreviewParams {
  minutesABlanc: number;
  minutesRecouche: number;
  minutesRecoucheFond: number;
  minutesArrivee: number;
  comfortFloorEquivalents: number;
  earliestStart: string;
  defaultStart: string;
  latestEnd: string;
  heavyArrivals: number;
  checkInEarly: string;
  checkInNormal: string;
  /** Heads/time trade-off (ADR-0015 §4). */
  maxHeads: number;
  targetFinish: string | null;
}

const previewFromRow = (row: WorkContentRow): PreviewParams => ({
  minutesABlanc: row.minutes_a_blanc,
  minutesRecouche: row.minutes_recouche,
  minutesRecoucheFond: row.minutes_recouche_fond,
  minutesArrivee: row.minutes_arrivee,
  comfortFloorEquivalents: row.comfort_floor_equivalents,
  earliestStart: timeInputValue(row.earliest_start),
  defaultStart: timeInputValue(row.default_start),
  latestEnd: timeInputValue(row.latest_end),
  heavyArrivals: row.heavy_arrivals,
  checkInEarly: timeInputValue(row.check_in_early),
  checkInNormal: timeInputValue(row.check_in_normal),
  maxHeads: row.max_heads ?? 6,
  targetFinish: row.target_finish ? timeInputValue(row.target_finish) : null,
});

/**
 * What the current numbers actually produce, on a fixed reference day.
 *
 * The operator edits MINUTES but cares about PEOPLE, so this closes the loop:
 * change a coefficient and the head count and window move under your hand,
 * instead of the effect being discovered on the board a week later. It runs the
 * SAME pure resolver the board will use, so the editor cannot promise one thing
 * and the board do another.
 *
 * Rendered while EDITING as well as while reading — a preview you can only see
 * after saving is a report, not a preview.
 */
function Preview({ params }: { params: PreviewParams }) {
  const minutes = {
    aBlanc: params.minutesABlanc,
    recouche: params.minutesRecouche,
    recoucheAFond: params.minutesRecoucheFond,
    arrivee: params.minutesArrivee,
  };
  // A half-typed field (empty input → NaN) must not throw or print « NaN
  // personnes »: say so plainly and wait for a usable number.
  const usable =
    Object.values(minutes).every((m) => Number.isFinite(m) && m >= 0) &&
    Number.isFinite(params.comfortFloorEquivalents) &&
    params.earliestStart !== '' &&
    params.defaultStart !== '' &&
    params.latestEnd !== '' &&
    minutes.aBlanc > 0;
  if (!usable) return <p className="equipe-hint">Aperçu indisponible — complétez les minutes et les horaires.</p>;

  const readyBy = checkInTime(PREVIEW.arrivees, {
    heavyArrivals: params.heavyArrivals,
    early: params.checkInEarly,
    normal: params.checkInNormal,
  });
  const spec = {
    kind: 'workContent' as const,
    activityId: 0,
    profileId: 0,
    label: 'aperçu',
    minutes,
    comfortFloorThreshold: params.comfortFloorEquivalents,
    earliestStart: params.earliestStart,
    defaultStart: params.defaultStart,
    latestEnd: params.latestEnd,
    maxHeads: params.maxHeads,
    ...(params.targetFinish ? { targetFinish: params.targetFinish } : {}),
  };
  const frontier = tradeoffFrontier(spec, PREVIEW, readyBy);
  const resolved = resolveMenage(
    {
      kind: 'workContent',
      activityId: 0,
      profileId: 0,
      label: 'aperçu',
      minutes,
      comfortFloorThreshold: params.comfortFloorEquivalents,
      earliestStart: params.earliestStart,
      defaultStart: params.defaultStart,
      latestEnd: params.latestEnd,
      maxHeads: params.maxHeads,
      ...(params.targetFinish ? { targetFinish: params.targetFinish } : {}),
    },
    PREVIEW,
    readyBy,
  );

  return (
    <>
      <p className="equipe-hint">
        Journée type ({PREVIEW.aBlancs} départs · {PREVIEW.recouches} recouches · {PREVIEW.arrivees} arrivées) :{' '}
        <strong>
          {resolved.heads} personne{resolved.heads > 1 ? 's' : ''} de {resolved.windowStart} à {resolved.windowEnd}
        </strong>{' '}
        — {resolved.workMinutes} min de travail, dont {resolved.criticalMinutes} avant le check-in de {readyBy} ·{' '}
        {aBlancEquivalents(PREVIEW, minutes).toFixed(1)} à blancs équivalents (plancher{' '}
        {comfortFloor(PREVIEW, params.comfortFloorEquivalents, minutes)}, capacité {resolved.capacityHeads}).
        {resolved.meetsTargetFinish === false && (
          <>
            {' '}
            <strong className="tradeoff-missed">
              Objectif {params.targetFinish} non atteignable à {params.maxHeads} personnes.
            </strong>
          </>
        )}
      </p>
      {/* The heads/time trade-off (ADR-0015 §4). Person-minutes are conserved
          under pairing, so work = heads × elapsed is an identity — this is the
          choice the operator actually makes each morning, and it was invisible
          while resolveMenage returned one answer with no alternatives. */}
      <div className="tradeoff">
        <span className="tradeoff-title">Effectif / durée — journée type</span>
        <ul className="tradeoff-list">
          {frontier.map((option) => (
            <li
              key={option.heads}
              className={`tradeoff-option${option.heads === resolved.heads ? ' is-chosen' : ''}${
                option.fitsInDay ? '' : ' is-over'
              }`}
            >
              <strong>{option.heads} pers.</strong> {option.windowStart}–{option.windowEnd}
              {!option.fitsInDay && <span className="tradeoff-flag"> dépasse {params.latestEnd}</span>}
              {!option.meetsDeadline && <span className="tradeoff-flag"> chambres pas prêtes à {readyBy}</span>}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label>
      <span>{label}</span>
      <input
        type="number"
        min={0}
        step="0.5"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label>
      <span>{label}</span>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
